import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { Box, Spinner } from '@factorialco/deprecated-design-system'
import { NewColor } from '@factorialco/f0-react'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  Add,
  Ai,
  Calendar,
  Delete,
  Envelope,
  ExternalLink,
  FileFilled,
  Pencil,
  Replace,
  StarFilled,
} from '@factorialco/f0-react/icons/app'
import {
  AtsApplicationsDataCollectionDocument,
  AtsApplicationsDataCollectionQuery,
  AtsHiringRolesEnum,
  AtsCandidatesDataCollectionTagsFilterDocument,
  BulkMoveToPhaseDocument,
  BulkMoveToPhaseMutation,
  JobPostingDocument,
} from 'generated/resources/graphql'
import { capitalize } from 'lodash'

import { observer } from 'mobx-react'
import { useWindowSize } from 'usehooks-ts'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import { useFeatures } from 'modules/core/hooks/useFeatures'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { AddApplicationsToJobPostingModal } from 'modules/ats/components/applications/AddApplicationsToJobPostingModal'
import { MoveToPhaseModal } from 'modules/ats/components/MoveToPhaseModal'
import { RejectApplicationsModal } from 'modules/ats/components/RejectApplicationsModal'
import { statusLabel } from 'modules/ats/components/AssessmentStatusTag'
import { SendEmailToApplicationsModal } from 'modules/ats/components/SendEmailToApplicationsModal'
import {
  offerLetterStatusLabel as getOfferLetterStatusLabel,
  offerLetterStatusColor,
} from 'modules/ats/components/OfferLetterStatusTag'
import { phaseColor } from 'modules/ats/components/ApplicationPhaseTag'
import { sortApplicationPhases } from 'modules/ats/lib/utils/applicationPhase'
import { useAtsContext } from 'modules/ats/lib/atsContext'
import { getSourceLabel } from 'modules/ats/lib/utils/candidateSources'

import { useQuestions } from 'modules/ats/hooks/questions/useQuestions'

interface Props {
  jobId: string
  allowsNewCandidates: boolean
}

type ApplicationItem =
  AtsApplicationsDataCollectionQuery['ats']['applicationsConnection']['edges'][number]['node']

type BulkAction = 'disqualify' | 'move-to-phase' | 'email' | 'add-to-job-opening' | null

const VALID_BULK_ACTIONS = new Set<string>([
  'disqualify',
  'move-to-phase',
  'email',
  'add-to-job-opening',
])

function isValidBulkAction(action: string): action is NonNullable<BulkAction> {
  return VALID_BULK_ACTIONS.has(action)
}

export const ApplicationsTable = observer(({ jobId, allowsNewCandidates }: Props) => {
  const { has_ats_bulk_actions } = useFeatures(['ats_bulk_actions'])
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const client = useApolloClient()
  const { access, company } = useCurrentSession()
  const { talentPoolByDefault } = useAtsContext()
  const [bulkAction, setBulkAction] = useState<BulkAction>(null)
  const [selectedApplications, setSelectedApplications] = useState<ApplicationItem[]>([])
  const containerRef = useRef<HTMLDivElement>(null)
  const [availableHeight, setAvailableHeight] = useState<number>()
  const size = useWindowSize()

  const [bulkMoveToPhase] = useMutation(BulkMoveToPhaseDocument, {
    extractorFn: (data: BulkMoveToPhaseMutation) => data.atsMutations.bulkMoveToPhaseAtsApplication,
  })

  const { data, loading } = useQuery(JobPostingDocument, {
    variables: { atsJobPostingId: jobId, accessId: access.id, companyId: company.id },
  })

  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return
    const top = el.getBoundingClientRect().top
    setAvailableHeight(window.innerHeight - top)
  }, [size.height, loading])

  const jobPosting = data?.ats.jobPosting
  const canManageJobPostings = data?.ats._policies.manageJobPostings.can ?? false
  const hiringRole: AtsHiringRolesEnum = jobPosting?.currentHiringRole[0]?.hiringRole ?? 'editor'
  const isReviewerRole = hiringRole === 'reviewer' && !canManageJobPostings

  const canAddCandidates = allowsNewCandidates && !isReviewerRole

  const { questions } = useQuestions({ atsJobPostingIds: [jobId] })

  // Synchronously pre-read stored visualization filters from localStorage so
  // they can be used as the initial currentFilters value. This prevents a
  // race condition where the async storage restoration triggers a
  // lanesProvider recompute while isLoading is still true, causing the stale
  // isLoading value to be captured and persisted – resulting in infinite
  // loading spinners in each kanban lane after back-navigation.
  //
  // Returning `undefined` (instead of `{}`) when there is no stored value lets
  // f0 fall back to `defaultFilters` internally without the prop overriding
  // it via its deep-compare effect. Injecting any extra key here (e.g. a
  // synthetic `qualified: [true]`) would diverge from what storage restoration
  // produces and re-introduce the race that #95879 fixed.
  const initialCurrentFilters = useMemo(() => {
    try {
      const raw = localStorage.getItem(`datacollection-ats/job-applications-${jobId}/v1`)
      if (!raw) return undefined
      const stored = JSON.parse(raw)
      const vizIndex = stored.visualization ?? 0
      return stored.visualizationFilters?.[String(vizIndex)] ?? undefined
    } catch {
      return undefined
    }
  }, [jobId])

  const questionsRef = useRef(questions)
  questionsRef.current = questions

  const rejectedApplicationsCount =
    jobPosting != null
      ? jobPosting.applicationsCount - jobPosting.activeApplicationsCount
      : undefined

  const hasOfferLetterFeature =
    data?.features.enabledFeatures.nodes.find((node) => node.name === 'ats_offer_letter')
      ?.enabled ?? false
  const hasAssessmentFeature =
    data?.features.enabledFeatures.nodes.find((node) => node.name === 'ats_ideal_disc_integration')
      ?.enabled ?? false
  const hasDuplicationServiceFeature =
    data?.features.enabledFeatures.nodes.find(
      (node) => node.name === 'dev_ats_duplication_identification_service'
    )?.enabled ?? false

  const canBulkActions = !isReviewerRole && has_ats_bulk_actions

  const offerLetterLabels = getOfferLetterStatusLabel()

  const assessmentStatusColor: Record<string, NewColor> = {
    pending_to_create: 'yellow',
    pending_to_complete: 'yellow',
    failed_to_process: 'barbie',
    completed: 'viridian',
  }

  const applicationPhases = jobPosting?.applicationPhases ?? []

  const orderedApplicationPhases = useMemo(
    () => sortApplicationPhases(applicationPhases),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      JSON.stringify(
        applicationPhases.map((p) => ({
          id: p.id,
          name: p.name,
          position: p.position,
          hiringStagePosition: p.hiringStage?.position,
        }))
      ),
    ]
  )

  const phaseVariant = (stageName?: string): 'positive' | 'warning' | 'info' => {
    switch (stageName) {
      case 'screening':
      case 'hired':
        return 'positive'
      case 'interview':
      case 'offer':
        return 'warning'
      default:
        return 'info'
    }
  }

  const tagsFilterConfig = useMemo(
    () => ({
      type: 'in' as const,
      label: i18n.t('ats.candidates.list.filters.tags_label'),
      options: {
        cache: true,
        options: async () => {
          const result = await client.query({
            query: AtsCandidatesDataCollectionTagsFilterDocument,
            fetchPolicy: 'cache-first',
          })

          return (
            result.data?.ats.tags.map((tag) => ({
              label: tag.name,
              value: tag.id,
            })) || []
          )
        },
      },
    }),
    [client]
  )

  const questionFilters = useMemo(
    () =>
      questions.reduce<
        Record<
          string,
          {
            type: 'in' | 'search'
            label: string
            options?: { options: Array<{ label: string; value: string }> }
          }
        >
      >((acc, question) => {
        if (question.questionType === 'file') {
          return acc
        }

        const key = `question_${question.id}`
        if (
          question.questionType === 'single_choice' ||
          question.questionType === 'multiple_choice'
        ) {
          acc[key] = {
            type: 'in',
            label: question.label,
            options: {
              options:
                question.options?.map((option) => ({
                  label: option.text,
                  value: option.text,
                })) || [],
            },
          }
        } else {
          acc[key] = {
            type: 'search',
            label: question.label,
          }
        }

        return acc
      }, {}),
    [questions]
  )

  const source = useGraphqlDataCollectionSource(
    {
      lanes: orderedApplicationPhases.map((phase) => ({
        id: phase.id.toString(),
        filters: { applicationPhaseId: [phase.id] },
      })),
      document: AtsApplicationsDataCollectionDocument,
      mapData: (result) => result.ats.applicationsConnection.edges.map((e) => e.node),
      mapPagination: (result) => ({
        total: result.ats.applicationsConnection.totalCount ?? 0,
        cursor: result.ats.applicationsConnection.pageInfo.endCursor ?? '',
        hasNextPage: result.ats.applicationsConnection.pageInfo.hasNextPage ?? false,
      }),
      perPage: 100,
      paginationType: 'infinite-scroll',
      search: { enabled: true },
      sortings: {
        atsApplicationPhasePosition: {
          label: i18n.t('ats.job_details.applications.table_headers.phase'),
        },
        ratingAverage: {
          label: i18n.t('ats.job_details.applications.table_headers.rating'),
        },
        createdAt: {
          label: i18n.t('ats.job_details.applications.table_headers.added_on'),
        },
      },
      filters: {
        qualified: {
          type: 'in' as const,
          label: i18n.t('ats.job_details.applications.tabs.active'),
          hideSelector: true,
          options: {
            options: [
              { label: i18n.t('ats.job_details.applications.tabs.active'), value: true },
              { label: i18n.t('ats.job_details.applications.tabs.rejected'), value: false },
            ],
          },
        },
        applicationPhaseId: {
          type: 'in' as const,
          label: i18n.t('ats.job_details.applications.table_headers.phase'),
          hideSelector: true,
          options: {
            options: applicationPhases.map((phase) => ({
              label: phase.name,
              value: phase.id,
            })),
          },
        },
        tags: tagsFilterConfig,
        ...questionFilters,
      },
      mapVariables: ({ filters, search, sortings }) => {
        const qualified =
          Array.isArray(filters.qualified) && filters.qualified.length > 0
            ? Boolean(filters.qualified[0])
            : true
        const applicationPhaseId = filters.applicationPhaseId?.[0]
        const atsTagIds = filters.tags && filters.tags.length > 0 ? filters.tags : undefined

        const atsAnswers = questionsRef.current
          .map((question) => {
            const key = `question_${question.id}`
            const allFilters: Record<string, unknown> = { ...filters }
            const filterValue = allFilters[key]

            if (!filterValue) return null

            if (
              question.questionType === 'single_choice' ||
              question.questionType === 'multiple_choice'
            ) {
              const values = Array.isArray(filterValue) ? filterValue : [filterValue]
              if (!values.length) return null
              const value =
                question.questionType === 'multiple_choice'
                  ? JSON.stringify(values)
                  : String(values[0])
              return { questionId: question.id, value }
            }

            if (typeof filterValue === 'string') {
              return { questionId: question.id, value: filterValue }
            }

            if (typeof filterValue === 'object' && 'value' in filterValue) {
              return { questionId: question.id, value: String(filterValue.value) }
            }

            return null
          })
          .filter(Boolean)

        return {
          atsJobPostingId: jobId,
          atsTagIds,
          atsApplicationPhaseId: applicationPhaseId,
          qualified,
          search: search || undefined,
          atsAnswers: atsAnswers.length > 0 ? atsAnswers : undefined,
          sort: sortings?.[0],
        }
      },
      itemUrl: (application: ApplicationItem) =>
        navigation.ats.jobs.jobsId.applications.applicationDetail.fullPath({
          jobId,
          application_id: application.id,
        }),
      primaryActions: () => {
        if (!canAddCandidates) return undefined

        return {
          label: i18n.t('ats.job_details.add_candidate.add_candidate'),
          icon: Add,
          onClick: () =>
            navigate(
              navigation.ats.jobs.jobsId.applications.new.fullPath({
                jobId,
              })
            ),
        }
      },
      secondaryActions: canAddCandidates
        ? {
            expanded: 0,
            actions: () => [
              {
                label: i18n.t('ats.job_details.add_candidate.add_internal_candidate'),
                description: i18n.t(
                  'ats.job_details.add_candidate.add_internal_candidate_description'
                ),
                icon: Pencil,
                onClick: () =>
                  navigate(
                    navigation.ats.jobs.jobsId.applications.newEmployeeCandidate.fullPath({
                      jobId,
                    })
                  ),
              },
              {
                label: i18n.t('ats.job_details.add_candidate.upload_cvs'),
                description: i18n.t('ats.job_details.add_candidate.upload_cvs_description'),
                icon: FileFilled,
                onClick: () =>
                  navigate(
                    navigation.ats.jobs.jobsId.applications.newFromCv.fullPath({
                      jobId,
                    })
                  ),
              },
            ],
          }
        : undefined,
      selectable: canBulkActions ? (application) => application.id : undefined,
      bulkActions: canBulkActions
        ? (selectedRows) => {
            const selectedItems = selectedRows.itemsStatus.filter((item) => item.checked)
            const items = selectedItems.map((item) => item.item)
            const everyCandidateWithoutEmail = items.every(
              (application) => !application.candidate?.email
            )
            const hasAnyRejected = items.some((application) => !application.qualified)
            return {
              primary: [
                !hasAnyRejected && {
                  id: 'disqualify',
                  label: i18n.t('ats.candidates.application_view.disqualify'),
                  icon: Delete,
                },
              ].filter(Boolean),
              secondary: [
                !everyCandidateWithoutEmail && {
                  id: 'email',
                  label: i18n.t('ats.candidates.application_view.send_email'),
                  icon: Envelope,
                },
                !hasAnyRejected && {
                  id: 'move-to-phase',
                  label: i18n.t('ats.candidates.application_view.move_to_phase'),
                  icon: Replace,
                },
                {
                  id: 'add-to-job-opening',
                  label: i18n.t('ats.candidates.application_view.add_to_job_opening'),
                  icon: Add,
                },
              ].filter(Boolean),
            }
          }
        : undefined,
      currentFilters: initialCurrentFilters,
      defaultFilters: { qualified: [true] },
    },
    [canAddCandidates, canBulkActions, jobId, client, orderedApplicationPhases]
  )

  const isRejectedPreset =
    Array.isArray(source.currentFilters?.qualified) && source.currentFilters.qualified[0] === false

  const selectedApplicationIds = useMemo(
    () => selectedApplications.map((application) => application.id),
    [selectedApplications]
  )

  const talentPoolVisible = useMemo(
    () => selectedApplications.some((application) => !application.candidate?.consentToTalentPool),
    [selectedApplications]
  )

  const talentPoolValue = useMemo(
    () =>
      selectedApplications.some((application) => application.candidate?.consentToTalentPool) ||
      talentPoolByDefault,
    [selectedApplications, talentPoolByDefault]
  )

  const handleBulkAction = (
    action: string,
    selectedRows: { itemsStatus: ReadonlyArray<{ item: ApplicationItem; checked: boolean }> }
  ) => {
    const selectedItems = selectedRows.itemsStatus.filter((item) => item.checked)
    const items = selectedItems.map((item) => item.item)
    setSelectedApplications(items)

    if (!action) return
    if (isValidBulkAction(action)) {
      setBulkAction(action)
    }
  }

  const resetBulkAction = useCallback(() => {
    setBulkAction(null)
    setSelectedApplications([])
    void client.refetchQueries({
      include: [AtsApplicationsDataCollectionDocument, JobPostingDocument],
    })
  }, [client])

  const columns = useMemo(() => {
    return [
      {
        label: i18n.t('ats.job_details.applications.table_headers.name'),
        render: (application: ApplicationItem) => application.candidate.fullName,
      },
      {
        label: i18n.t('ats.job_details.applications.table_headers.phase'),
        sorting: 'atsApplicationPhasePosition' as const,
        render: (application: ApplicationItem) => {
          const hasOfferLetter = hasOfferLetterFeature && !!application.offerLetter
          const tags = [
            {
              text: application.applicationPhase.name,
              color: phaseColor(application.applicationPhase.hiringStage?.name || 'default'),
            },
          ]

          if (hasOfferLetter && application.offerLetter?.status) {
            tags.push({
              text: offerLetterLabels[application.offerLetter.status] ?? '',
              color: offerLetterStatusColor[application.offerLetter.status] ?? 'smoke',
            })
          }

          if (hasAssessmentFeature && application.assessment && !hasOfferLetter) {
            tags.push({
              text: statusLabel(application.assessment.status),
              color: assessmentStatusColor[application.assessment.status] ?? 'smoke',
            })
          }

          return {
            type: 'tagList' as const,
            value: {
              type: 'dot' as const,
              max: 2,
              tags: tags.filter((tag) => tag.text),
            },
          }
        },
      },
      {
        label: i18n.t('ats.job_details.applications.table_headers.added_on'),
        sorting: 'createdAt' as const,
        render: (application: ApplicationItem) => dateBackendDateTimeToUI(application.createdAt),
      },
      {
        label: i18n.t('ats.job_details.applications.table_headers.source'),
        render: (application: ApplicationItem) => {
          const sourceLabel = getSourceLabel(application.applicationSource?.name)
          if (application.applicationSource?.name && application.medium) {
            const mediumLabel = capitalize(application.medium)
            return `${mediumLabel} (${sourceLabel})`
          }
          return sourceLabel
        },
      },
      ...(hasDuplicationServiceFeature
        ? [
            {
              label: i18n.t('ats.job_details.applications.table_headers.alerts'),
              render: (application: ApplicationItem) => {
                const hasDuplicates =
                  (application.candidate?.duplicateIdentificationsAsMatched?.length ?? 0) > 0 ||
                  (application.candidate?.duplicateIdentificationsAsSource?.length ?? 0) > 0

                if (!hasDuplicates) {
                  return undefined
                }

                return i18n.t('ats.candidates.list.filters.alerts_options.duplicate')
              },
            },
          ]
        : []),
      {
        label: i18n.t('ats.job_details.applications.table_headers.rating'),
        sorting: 'ratingAverage' as const,
        render: (application: ApplicationItem) => {
          if (!application.ratingAverage) {
            return '-'
          }

          const rating = Math.round(application.ratingAverage)
          const status = rating >= 4 ? 'positive' : rating === 3 ? 'warning' : 'critical'

          return {
            type: 'status' as const,
            value: {
              status,
              label: rating.toString(),
            },
          }
        },
      },
      {
        label: i18n.t('ats.applications_table.top_match'),
        render: (application: ApplicationItem) => {
          const showTopMatch =
            application.summary?.topMatch &&
            application.openAiFeedbackTopMatchRating?.helpful !== false

          if (!showTopMatch) {
            return '-'
          }

          return {
            type: 'tagList' as const,
            value: {
              type: 'raw' as const,
              tags: [{ text: i18n.t('ats.applications_table.top_match'), icon: Ai }],
              max: 1,
            },
          }
        },
      },
    ]
  }, [
    assessmentStatusColor,
    hasAssessmentFeature,
    hasDuplicationServiceFeature,
    hasOfferLetterFeature,
    offerLetterLabels,
    offerLetterStatusColor,
  ])

  const kanbanLanes = useMemo(
    () =>
      orderedApplicationPhases.map((phase) => ({
        id: phase.id.toString(),
        title: phase.name,
        variant: phaseVariant(phase.hiringStage?.name || 'default'),
      })),
    [orderedApplicationPhases]
  )

  const setFlashMessage = (success: boolean) => {
    const type = success ? 'correct' : 'wrong'
    const message = success ? i18n.t('common.saved_successfully') : i18n.t('errors.generic_error')

    app.setFlash({
      type: type,
      message: message,
      time: 2000,
    })
  }

  const handleMove = useCallback(
    async (
      fromLaneId: string,
      toLaneId: string,
      sourceRecord: ApplicationItem
    ): Promise<ApplicationItem> => {
      if (fromLaneId === toLaneId) return sourceRecord

      const response = await bulkMoveToPhase({
        variables: {
          ids: [sourceRecord.id],
          authorId: access.id,
          atsApplicationPhaseId: toLaneId,
          skipSmartAction: false,
        },
        refetchQueries: [AtsApplicationsDataCollectionDocument, JobPostingDocument],
      })

      const result = handleResult(response, () => undefined)
      setFlashMessage(result.success)

      if (!result.success) {
        throw new Error(i18n.t('errors.generic_error'))
      }

      const updatedApplication = result.data.applications?.[0]
      if (updatedApplication) {
        return {
          ...sourceRecord,
          applicationPhase: {
            ...sourceRecord.applicationPhase,
            ...updatedApplication.applicationPhase,
          },
        }
      }

      return sourceRecord
    },
    [access.id, bulkMoveToPhase]
  )

  if (loading || !jobPosting) {
    return (
      <Box padding="s40" width="full" alignItems="center">
        <Spinner />
      </Box>
    )
  }

  return (
    <Box
      ref={containerRef}
      width="full"
      style={availableHeight ? { height: `${availableHeight - 1}px` } : undefined}
    >
      <OneDataCollection
        id={`ats/job-applications-${jobId}/v1`}
        fullHeight
        source={source}
        storage={{ features: ['search', 'visualization', 'visualizationFilters'] }}
        onBulkAction={canBulkActions ? handleBulkAction : undefined}
        visualizations={[
          {
            type: 'kanban',
            filters: {
              qualified: {
                type: 'in' as const,
                label: i18n.t('ats.job_details.applications.tabs.active'),
                hideSelector: true,
                options: {
                  options: [
                    { label: i18n.t('ats.job_details.applications.tabs.active'), value: true },
                    { label: i18n.t('ats.job_details.applications.tabs.rejected'), value: false },
                  ],
                },
              },
              tags: tagsFilterConfig,
              ...questionFilters,
            },
            presets: [
              {
                label: i18n.t('ats.job_details.applications.tabs.active'),
                filter: { qualified: [true] },
                itemsCount: () => jobPosting?.activeApplicationsCount ?? undefined,
              },
              {
                label: i18n.t('ats.job_details.applications.tabs.rejected'),
                filter: { qualified: [false] },
                itemsCount: () => rejectedApplicationsCount ?? undefined,
              },
            ],
            options: {
              lanes: kanbanLanes,
              onMove: canBulkActions && !isRejectedPreset ? handleMove : undefined,
              title: (application: ApplicationItem) => application.candidate.fullName,
              avatar: (application: ApplicationItem) => ({
                type: 'person' as const,
                firstName: application.candidate.firstName,
                lastName: application.candidate.lastName,
              }),
              metadata: (application: ApplicationItem) => {
                const ratingLabel = application.ratingAverage
                  ? Math.round(application.ratingAverage).toString()
                  : '-'

                const ratingValue = application.ratingAverage
                  ? {
                      type: 'status' as const,
                      label: ratingLabel,
                      value: {
                        status:
                          Math.round(application.ratingAverage) >= 4
                            ? ('positive' as const)
                            : Math.round(application.ratingAverage) === 3
                              ? ('warning' as const)
                              : ('critical' as const),
                        label: ratingLabel,
                      },
                    }
                  : {
                      type: 'text' as const,
                      label: ratingLabel,
                      value: '-',
                    }

                const sourceLabel = getSourceLabel(application.applicationSource?.name)
                const sourceValue =
                  application.applicationSource?.name && application.medium
                    ? `${capitalize(application.medium)} (${sourceLabel})`
                    : sourceLabel

                const showTopMatch =
                  application.summary?.topMatch &&
                  application.openAiFeedbackTopMatchRating?.helpful !== false

                return [
                  {
                    icon: StarFilled,
                    property: ratingValue,
                  },
                  {
                    icon: ExternalLink,
                    property: {
                      type: 'text' as const,
                      label: sourceValue,
                      value: sourceValue,
                    },
                  },
                  {
                    icon: Calendar,
                    property: {
                      type: 'text' as const,
                      label: dateBackendDateTimeToUI(application.createdAt),
                      value: dateBackendDateTimeToUI(application.createdAt),
                    },
                  },
                  ...(showTopMatch
                    ? [
                        {
                          icon: Ai,
                          property: {
                            type: 'tagList' as const,
                            label: i18n.t('ats.applications_table.top_match'),
                            value: {
                              type: 'raw' as const,
                              tags: [{ text: i18n.t('ats.applications_table.top_match') }],
                              max: 1,
                            },
                          },
                        },
                      ]
                    : []),
                ]
              },
            },
          },
          {
            type: 'table',
            filters: {
              qualified: {
                type: 'in',
                label: i18n.t('ats.job_details.applications.tabs.active'),
                hideSelector: true,
                options: {
                  options: [
                    { label: i18n.t('ats.job_details.applications.tabs.active'), value: true },
                    { label: i18n.t('ats.job_details.applications.tabs.rejected'), value: false },
                  ],
                },
              },
              tags: tagsFilterConfig,
              applicationPhaseId: {
                type: 'in',
                label: i18n.t('ats.job_details.applications.table_headers.phase'),
                hideSelector: true,
                options: {
                  options:
                    jobPosting?.applicationPhases?.map((phase) => ({
                      label: phase.name,
                      value: phase.id,
                    })) || [],
                },
              },
              ...questionFilters,
            },
            presets: [
              {
                label: i18n.t('ats.job_details.applications.tabs.active'),
                filter: { qualified: [true] },
              },
              ...orderedApplicationPhases.map((phase) => ({
                label: phase.name,
                filter: { qualified: [true], applicationPhaseId: [phase.id] },
                itemsCount: () => phase.activeApplicationsCount ?? undefined,
              })),
              {
                label: i18n.t('ats.job_details.applications.tabs.rejected'),
                filter: { qualified: [false], applicationPhaseId: [] },
                itemsCount: () => rejectedApplicationsCount ?? undefined,
              },
            ],
            options: {
              frozenColumns: 1,
              columns,
            },
          },
        ]}
      />

      {bulkAction === 'disqualify' && (
        <RejectApplicationsModal
          jobPostingId={jobId}
          selectedApplications={selectedApplications}
          title={i18n.tp('ats.candidates.applications.bulk_disqualify.title', {
            count: selectedApplications.length,
          })}
          onSuccess={resetBulkAction}
          onClose={resetBulkAction}
          saveToTalentPoolCheckboxValue={talentPoolValue}
          saveToTalentPoolCheckboxVisible={talentPoolVisible}
        />
      )}

      {bulkAction === 'move-to-phase' && (
        <MoveToPhaseModal
          jobId={jobId}
          applications={selectedApplications}
          onSuccess={resetBulkAction}
          onClose={resetBulkAction}
          hiringRole={hiringRole}
        />
      )}

      {bulkAction === 'add-to-job-opening' && (
        <AddApplicationsToJobPostingModal
          applicationIds={selectedApplicationIds}
          onSuccess={resetBulkAction}
          onClose={resetBulkAction}
        />
      )}

      {bulkAction === 'email' && (
        <SendEmailToApplicationsModal
          onSuccess={resetBulkAction}
          selectedApplications={selectedApplications}
          onClose={resetBulkAction}
        />
      )}

      <Outlet />
    </Box>
  )
})
